import { useEffect, useMemo, useState } from 'react'
import { fetchStudentCareerAssessment } from 'api/users'
import {
	buildBlindspots,
	buildCampusVibe,
	buildExpandingStrengths,
	buildPersonalityType,
	buildProfileFromTraitScores,
	buildSuperProfile,
	buildSuperpowers,
} from '../utils/personalityV3Utils'

const normalizeList = value => {
	if (Array.isArray(value)) return value
	if (!value) return []
	return [value]
}

export const useCareersTab = (studentId, isActive = false) => {
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState(null)
	const [assessment, setAssessment] = useState(null)

	useEffect(() => {
		if (!studentId || !isActive) return undefined
		let alive = true
		setAssessment(null)
		const run = async () => {
			try {
				setIsLoading(true)
				setError(null)
				const data = await fetchStudentCareerAssessment(studentId)
				if (!alive) return
				setAssessment(data || null)
			} catch (e) {
				if (!alive) return
				setError(e?.message || 'Failed to load career quiz data')
				setAssessment(null)
			} finally {
				if (alive) setIsLoading(false)
			}
		}
		run()
		return () => {
			alive = false
		}
	}, [studentId, isActive])

	const personalityRaw = useMemo(() => {
		const src =
			assessment?.dominantType ||
			assessment?.personalityType ||
			assessment?.assessment?.dominantType ||
			assessment?.assessment?.personalityType ||
			null
		if (!src) return null
		if (typeof src === 'string') {
			return { name: src, description: '', tags: [] }
		}
		return {
			name: src?.name || 'Personality Type',
			description: src?.description || '',
			tags: normalizeList(src?.tags),
		}
	}, [assessment])

	const traitScores = useMemo(
		() =>
			assessment?.traitScores ||
			assessment?.analysis?.traitScores ||
			assessment?.assessment?.traitScores ||
			{},
		[assessment],
	)

	const profile = useMemo(() => buildProfileFromTraitScores(traitScores), [traitScores])

	const hasTraitScores = useMemo(
		() => Object.values(profile || {}).some(v => Number(v) > 0),
		[profile],
	)

	const isNotCompleted = useMemo(() => {
		// Student has not completed the quiz at all: no trait scores and no personality type.
		return !hasTraitScores && !personalityRaw
	}, [hasTraitScores, personalityRaw])

	const personality = useMemo(() => {
		if (!personalityRaw && !hasTraitScores) return null
		if (!hasTraitScores) return personalityRaw
		return buildPersonalityType(profile, personalityRaw)
	}, [profile, personalityRaw, hasTraitScores])

	const superProfile = useMemo(
		() => (hasTraitScores ? buildSuperProfile(profile) : {}),
		[profile, hasTraitScores],
	)
	const superpowers = useMemo(
		() => (hasTraitScores ? buildSuperpowers(profile) : []),
		[profile, hasTraitScores],
	)
	const blindspots = useMemo(
		() => (hasTraitScores ? buildBlindspots(profile) : []),
		[profile, hasTraitScores],
	)
	const campusVibe = useMemo(
		() => (hasTraitScores ? buildCampusVibe(profile) : null),
		[profile, hasTraitScores],
	)
	const expandingStrengths = useMemo(
		() => (hasTraitScores ? buildExpandingStrengths(profile) : []),
		[profile, hasTraitScores],
	)

	return {
		isLoading,
		error,
		isNotCompleted,
		personality,
		profile,
		superProfile,
		superpowers,
		blindspots,
		campusVibe,
		expandingStrengths,
		hasTraitScores,
	}
}

export default useCareersTab
